import { CallIntelTypes } from '@frontend/api-call-intel';
import { createStoreWithSubscribe, createShallowStore } from '@frontend/store';

// PanelContext: State related to a specific panel source
export type PanelContext = {
  allCalls: CallIntelTypes.Call[];
  defaultAccordion: CallIntelTypes.CallTakeawayAccordion;
  editedCallIds: string[];
  isDemoAccount?: boolean;
  nextCall: CallIntelTypes.Call | null;
  previousCall: CallIntelTypes.Call | null;
  selectedCall: CallIntelTypes.Call | null;
  selectedCallMetaData: CallIntelTypes.CallMetadata | null;
  selectedDemoTasks: CallIntelTypes.CallTaskListResponse | null;
  unsavedEditChangesConfig: CallIntelTypes.UnsavedEditChangesConfig;
};

// Default context for initializing a new panel source
export const defaultPanelContext: PanelContext = {
  defaultAccordion: 'call-summary',
  editedCallIds: [],
  isDemoAccount: false,
  nextCall: null,
  previousCall: null,
  selectedCall: null,
  selectedCallMetaData: null,
  selectedDemoTasks: null,
  unsavedEditChangesConfig: {
    actionType: 'NO_ACTION',
    dontAskAgain: false,
    entity: '',
    hasUnsavedChanges: false,
  },
  allCalls: [],
};

type PanelContextsState = {
  activeSource: CallIntelTypes.PanelSource | null;
  contexts: Record<CallIntelTypes.PanelSource, PanelContext>;
};

// All actions for manipulating the panel store, grouped by concern
type CallTakeawayPanelActions = {
  closeContext: () => void;
  handleNextClick: (params: { source: CallIntelTypes.PanelSource }) => void;
  handlePreviousClick: (params: { source: CallIntelTypes.PanelSource }) => void;
  openContext: (params: OpenContextParams) => void;
  reset: () => void;
  setAllCalls: (params: { calls: CallIntelTypes.Call[]; source: CallIntelTypes.PanelSource }) => void;
  setDefaultAccordion: (params: {
    accordion: CallIntelTypes.CallTakeawayAccordion;
    source: CallIntelTypes.PanelSource;
  }) => void;
  setEditedCallIds: (params: { callIds: string[]; source: CallIntelTypes.PanelSource }) => void;
  setIsDemoAccount: (params: { isDemoAccount: boolean; source: CallIntelTypes.PanelSource }) => void;
  setSelectedCall: (params: { call: CallIntelTypes.Call | null; source: CallIntelTypes.PanelSource }) => void;
  setSelectedCallMetaData: (params: {
    callMetaData: CallIntelTypes.CallMetadata | null;
    source: CallIntelTypes.PanelSource;
  }) => void;
  setSelectedDemoTasks: (params: { tasks: CallIntelTypes.Task[] | null; source: CallIntelTypes.PanelSource }) => void;
  setUnsavedEditChangesConfig: (params: {
    config: CallIntelTypes.UnsavedEditChangesConfig;
    source: CallIntelTypes.PanelSource;
  }) => void;
};

type OpenContextParams = {
  call: CallIntelTypes.Call;
  opts?: {
    calls?: CallIntelTypes.Call[];
    defaultAccordion?: CallIntelTypes.CallTakeawayAccordion;
    isDemoAccount?: boolean;
  };
  source: CallIntelTypes.PanelSource;
};

export interface CallTakeawayPanelStore extends PanelContextsState, CallTakeawayPanelActions {}

// Helper to get next/previous call in a list
const getAdjacentCalls = (calls: CallIntelTypes.Call[], currentCallId: string) => {
  const idx = calls.findIndex((c) => c.id === currentCallId);
  return {
    nextCall: idx >= 0 ? calls[idx + 1] || null : null,
    previousCall: idx > 0 ? calls[idx - 1] || null : null,
  };
};

// Main store implementation
export const useCallTakeawayPanelStore = createStoreWithSubscribe<CallTakeawayPanelStore>(
  (set) => ({
    contexts: {} as Record<CallIntelTypes.PanelSource, PanelContext>,
    activeSource: null,

    // Open a context for a given source and call, with options
    openContext: ({ call, opts, source }: OpenContextParams) => {
      set((state) => {
        const prev = state.contexts[source] ?? defaultPanelContext;
        const allCalls = opts?.calls ?? prev.allCalls;
        const { nextCall, previousCall } = getAdjacentCalls(allCalls, call.id);

        state.activeSource = source;
        state.contexts[source] = {
          ...prev,
          allCalls,
          defaultAccordion: opts?.defaultAccordion ?? prev.defaultAccordion,
          isDemoAccount: opts?.isDemoAccount ?? prev.isDemoAccount,
          selectedCall: call,
          nextCall,
          previousCall,
        };
      });
    },

    // Set all calls for a source
    setAllCalls: ({ calls, source }) => {
      set((state) => {
        const ctx = state.contexts[source] ?? defaultPanelContext;
        state.contexts[source] = { ...ctx, allCalls: calls };
      });
    },

    // Set the selected call for a source, and update next/previous
    setSelectedCall: ({ call, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;

        if (call === null) {
          state.activeSource = source;
          state.contexts[source] = {
            ...ctx,
            selectedCall: null,
            nextCall: null,
            previousCall: null,
          };
          return;
        }

        const { nextCall, previousCall } = getAdjacentCalls(ctx.allCalls, call.id);

        state.activeSource = source;
        state.contexts[source] = {
          ...ctx,
          selectedCall: call,
          nextCall,
          previousCall,
        };
      });
    },

    // Set selected call metadata for a source
    setSelectedCallMetaData: ({ callMetaData, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, selectedCallMetaData: callMetaData };
      });
    },

    // Set selected demo tasks for a source
    setSelectedDemoTasks: ({ tasks, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, selectedDemoTasks: tasks ? { tasks } : null };
      });
    },

    // Set edited call IDs for a source
    setEditedCallIds: ({ callIds, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, editedCallIds: callIds };
      });
    },

    // Set unsaved edit changes config for a source
    setUnsavedEditChangesConfig: ({ config, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, unsavedEditChangesConfig: config };
      });
    },

    // Set default accordion for a source
    setDefaultAccordion: ({ accordion, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, defaultAccordion: accordion };
      });
    },

    // Set demo account flag for a source
    setIsDemoAccount: ({ isDemoAccount, source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        if (!ctx) return;
        state.contexts[source] = { ...ctx, isDemoAccount };
      });
    },

    // Handle navigation to next call for a source
    handleNextClick: ({ source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        const selectedCall = ctx?.selectedCall;
        if (!ctx || !selectedCall) return;
        const idx = ctx.allCalls.findIndex((c) => c.id === selectedCall.id);
        if (idx >= 0 && idx < ctx.allCalls.length - 1) {
          const nextCall = ctx.allCalls[idx + 1];
          const { nextCall: newNextCall, previousCall: newPrevCall } = getAdjacentCalls(ctx.allCalls, nextCall.id);
          state.activeSource = source;
          state.contexts[source] = {
            ...ctx,
            selectedCall: nextCall,
            nextCall: newNextCall,
            previousCall: newPrevCall,
          };
        }
      });
    },

    // Handle navigation to previous call for a source
    handlePreviousClick: ({ source }) => {
      set((state) => {
        const ctx = state.contexts[source];
        const selectedCall = ctx?.selectedCall;
        if (!ctx || !selectedCall) return;
        const idx = ctx.allCalls.findIndex((c) => c.id === selectedCall.id);
        if (idx > 0) {
          const prevCall = ctx.allCalls[idx - 1];
          const { nextCall: newNextCall, previousCall: newPrevCall } = getAdjacentCalls(ctx.allCalls, prevCall.id);
          state.activeSource = source;
          state.contexts[source] = {
            ...ctx,
            selectedCall: prevCall,
            nextCall: newNextCall,
            previousCall: newPrevCall,
          };
        }
      });
    },

    // Close the current context (panel)
    closeContext: () => {
      set((state) => {
        state.activeSource = null;
      });
    },

    // Reset all contexts and active source
    reset: () => {
      set((state) => {
        state.contexts = {} as Record<CallIntelTypes.PanelSource, PanelContext>;
        state.activeSource = null;
      });
    },
  }),
  {
    name: 'CallInsightPanelStore',
    trace: true,
  }
);

export const useCallTakeawayPanelShallowStore = createShallowStore(useCallTakeawayPanelStore);
